import { Component, OnInit } from "@angular/core";
import { faPencil, faBroom } from "@fortawesome/free-solid-svg-icons";
import { FormField } from "src/app/interfaces/form-field.model";
import { CoeBiddingPreparationService } from "src/app/services/autopac/coe-bidding-preparation.service";
import { NotifierService } from "angular-notifier";
import { DatePipe } from "@angular/common";

interface CoeBiddingPreparationModel {
  EndingContractNo: number;
  FromDate: string;
  Option: number;
  StartingContractNo: number;
  StartingRefNo: number;
  ToDate: string;
  ArrangeBy?: string;
}

const TOGGLEABLE_FIELD_IDS = [
  "ArrangeBy",
  "StartingContractNo",
  "EndingContractNo",
  "FromDate",
  "ToDate",
  "StartingRefNo",
];

@Component({
  selector: "app-coe-bidding-preparation",
  templateUrl: "./coe-bidding-preparation.component.html",
  styleUrls: ["./coe-bidding-preparation.component.css"],
})
export class CoeBiddingPreparationComponent implements OnInit {
  // ICONS VARIABLES

  faPencil = faPencil;
  faBroom = faBroom;

  form: FormField[] = [
    {
      type: "select",
      id: "Option",
      value: 3,
      options: [
        {
          display: "With contract number",
          value: 1,
        },
        {
          display: "Without contract number (buyer's order date)",
          value: 2,
        },
        {
          display: "Without contract number (buyer's order number)",
          value: 3,
        },
      ],
    },
    {
      type: "select",
      id: "ArrangeBy",
      label: "Arrange by",
      required: false,
      lock: true,
      options: [
        {
          display: "Contract Number",
          value: "ContractNumber",
        },
        {
          display: "COE Category",
          value: "COECategory",
        },
      ],
    },
    {
      type: "number",
      id: "StartingContractNo",
      label: "Starting contract number",
      value: 0,
      lock: true,
    },
    {
      type: "number",
      id: "EndingContractNo",
      label: "Ending contract number",
      value: 0,
      lock: true,
    },
    {
      type: "date",
      id: "FromDate",
      label: "From buyer's order date (dd/mm/yyyy)",
      lock: true,
    },
    {
      type: "date",
      id: "ToDate",
      label: "To buyer's order date (dd/mm/yyyy)",
      lock: true,
    },
    {
      type: "number",
      id: "StartingRefNo",
      label: "Starting buyer's order number",
      lock: false,
    },
  ];

  constructor(
    private coeBiddingPreparationService: CoeBiddingPreparationService,
    private datePipe: DatePipe,
    private notifier: NotifierService,
  ) {}

  ngOnInit() {}

  updateFields(event: any): void {
    this.form = event.fields;

    TOGGLEABLE_FIELD_IDS.forEach((id) => {
      this.getField(id).lock = true;
    });

    const option = this.getField("Option").value;
    this.getField("ArrangeBy").required = false;

    switch (option) {
      case 1:
        this.getField("ArrangeBy").lock = false;
        this.getField("StartingContractNo").lock = false;
        this.getField("EndingContractNo").lock = false;
        this.getField("ArrangeBy").required = true;
        break;
      case 2:
        this.getField("FromDate").lock = false;
        this.getField("ToDate").lock = false;
        break;
      case 3:
        this.getField("StartingRefNo").lock = false;
        break;
    }
  }

  export(): void {
    const fromDateRaw = this.getFieldValue("FromDate");
    const toDateRaw = this.getFieldValue("ToDate");

    const FromDate = fromDateRaw
      ? this.datePipe.transform(fromDateRaw, "dd/MM/yyyy")
      : "";
    const ToDate = toDateRaw
      ? this.datePipe.transform(toDateRaw, "dd/MM/yyyy")
      : "";

    const model: CoeBiddingPreparationModel = {
      EndingContractNo: this.getFieldValue("EndingContractNo"),
      FromDate: FromDate ?? "",
      Option: this.getFieldValue("Option"),
      StartingContractNo: this.getFieldValue("StartingContractNo"),
      StartingRefNo: this.getFieldValue("StartingRefNo"),
      ToDate: ToDate ?? "",
    };

    switch (this.getField("Option").value) {
      case 1:
        model.ArrangeBy = this.getFieldValue("ArrangeBy");
        if (model.StartingContractNo >= model.EndingContractNo) {
          this.notifier.notify(
            "error",
            "Ending Contract Number must be greater than Starting Contract Number",
          );
          return;
        }
        break;
      case 2:
        if (!(FromDate || ToDate)) {
          this.notifier.notify(
            "error",
            "Please specify From Date and To Date values",
          );
          return;
        }
        break;
      case 3:
        if (!model.StartingRefNo) {
          this.notifier.notify(
            "error",
            "Please specify a valid starting buyer's order number",
          );
          return;
        }
        break;
    }

    const fromComp = this.datePipe.transform(
      fromDateRaw,
      "yyyy-MM-dd",
    );
    const toComp = this.datePipe.transform(toDateRaw, "yyyy-MM-dd");

    if (fromComp && toComp && fromComp >= toComp) {
      this.notifier.notify(
        "error",
        "To Buyers Order Date must be greater than From Buyers Order Date",
      );
      return;
    }

    this.coeBiddingPreparationService
      .COEBiddingPreparation(model)
      .subscribe({
        next: (response) => {
          if (response.body.Error) {
            this.notifier.notify(
              "error",
              "Please add valid date time values",
            );
          } else {
            window.open(response.body.Value, "_blank");
          }
        },
        error: (error) => console.log(error),
      });
  }

  getField(id: string): FormField {
    return (
      this.form.find((f) => f.id === id) || {
        id: "",
        type: "text",
        label: "",
        value: undefined,
      }
    );
  }

  getFieldValue(id: string): any {
    const field = this.getField(id);
    if (field.type === "number")
      return field.value ? Number(field.value) : field.value;
    return field.value;
  }

  clear() {
    this.form.forEach((item) => {
      item.value = undefined;
    });
  }
}
